/**
 * The entities module contains all the entities used in the calculation
 * customization domain.
 *
 * These entities have had their base database fields adapted and trimmed to a
 * standardized set required for the domain. For example, we rename a few
 * userAuthorId instances to the more common authorUserId.
 *
 * Additional fields, including linkages between these entities, will be
 * resolved by additional domain logic hooked up to graphQL resolvers for these
 * types.
 */
import { makeBetterEnum } from '@watershed/constants/betterEnum';

import { CalculationModel as CalculationModelDocument } from '../../calculationModel/calculationModelSchema';
import { ReferenceDataSchemaDefinition } from '../../referenceData/ReferenceDataSchemaInterface';

/**
 * The base interface for all entities in the calculation customization domain.
 *
 * This interface is used to ensure that all entities in the calculation
 * customization domain have a consistent set of base fields.
 */
export interface BaseEntity {
  id: string;
  displayName: string;
  orgId: string | null;
  createdAt: Date;
  updatedAt: Date;
  authorUserId: string | null;
  authorApiKeyId: string | null;
}

interface BaseEntityWithoutAuthorApiKeyId extends BaseEntity {
  authorApiKeyId: null;
}

interface BaseEntityWithoutAuthorIds extends BaseEntity {
  authorUserId: null;
  authorApiKeyId: null;
}

/**
 * A collection of calculation methods (née stable emissions models) selected by
 * the user during footprint generation. This entity is a container of top-level
 * calculation methods.
 */
export interface CalculationMethod extends BaseEntityWithoutAuthorApiKeyId {
  businessActivityTypeName: string;
  description: string | null;
  isOrgGated: boolean;
}

/**
 * A binding is a parameter to a calculation model matcher.
 */
export interface CalculationModelMatcherBinding {
  name: string;
  expression: string;
}

/**
 * A matcher bridges a calculation method bundle to a calculation method.
 */
export interface CalculationModelMatcher extends BaseEntityWithoutAuthorIds {
  emissionsModelStableId: string;
  businessActivityTypeId: string;
  filterExpression: string | null;
  quantitySpecifier: string | null;
  bindings: Array<CalculationModelMatcherBinding>;
}

interface BaseCalculationModel extends BaseEntityWithoutAuthorApiKeyId {
  description: string | null;
  methodFamilyId: string;
  methodFamilyVersion: string;
  emissionsModelVersionId: string;
  schemaVersion: string;
  calculationModel: CalculationModelDocument;
  calculationCustomizationSchemaRevisionId: string | null;
  isPhony: boolean;
}

/**
 * A consolidated representation of an emissions model.
 *
 * In this representation, 'stable' models and their most recent versions are
 * collapsed into a single entity.
 */
export interface RealCalculationModel extends BaseCalculationModel {
  isPhony: false;
}

/**
 * A phony calculation model is a display-only model constructed from the top
 * level models and matchers of a calculation method.
 */
export interface PhonyCalculationModel extends BaseCalculationModel {
  calculationCustomizationSchemaRevisionId: null;
  emissionsModelVersionId: '__phony_';
  isPhony: true;
}

/** Alias type for a calculation model of unknown phonyness. */
export type CalculationModel = RealCalculationModel | PhonyCalculationModel;

export interface CalculationModelParameterResolver
  extends BaseEntityWithoutAuthorApiKeyId {
  custom: boolean;
  default: boolean;
  emissionsModelVersionId: string;
  parameterExpressions: Array<CalculationModelParameterExpression>;
}

export type CalculationModelParameterExpression = {
  name: string;
  expression: string;
};

/**
 * The status of an entity that is potentially editable by the user.
 */
export const EntityStatus = makeBetterEnum('draft', 'published', 'archived');
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type EntityStatus = (typeof EntityStatus)[keyof typeof EntityStatus];

/**
 * Reference tables are (for a user) the top level entity for storing reference
 * data. They are analogous to admin reference data sources.
 */
export interface ReferenceTable extends BaseEntity {
  slug: string;
  description: string | null;
  url: string | null;
  status: EntityStatus;
  isCustomerCreated: boolean;
}

/**
 * A reference table version is analogous to admin reference data versions. They
 * have a schema, and only the top of the pile within their version is 'live'.
 * In calculation customization, we only have one revision per version.
 */
export interface ReferenceTableVersion extends BaseEntity {
  status: EntityStatus;
  sourceId: string;
  description: string | null;
  url: string | null;

  // From the paired revision
  revisionId: string;
  schema: ReferenceDataSchemaDefinition;
  outputFileId: string;
}

/**
 * Base citation fields common to both WS and customer citations.
 */
interface BaseCitation extends BaseEntity {
  slug: string;
  sourceNotes: string;
  sourceUrl: string;
  isSupplierSpecific: boolean;
  isCustomerProvided: boolean;
  status: EntityStatus;
  archivedAt: Date | null;

  // A tag by which to discriminate types down the line
  citationKind: 'ws' | 'customer';
}

/**
 * A citation record owned by core WS methodology.
 */
interface WSCitation extends BaseCitation {
  citationKind: 'ws';
  orgId: null;
  hasLicenseRestrictions: boolean;
  isCustomerProvided: false;
}

/**
 * A citation record owned by the customer.
 *
 * Exported for use in writeCitation; we don't allow WSCitations to be written
 * through calculation customization.
 */
export interface CustomerCitation extends BaseCitation {
  citationKind: 'customer';
  orgId: string;
  hasLicenseRestrictions: false;
  isCustomerProvided: true;
}

export type Citation = WSCitation | CustomerCitation;
